import React, { useState, useEffect } from 'react';
import { func, shape, string } from 'prop-types';
import moment from 'moment';
import { Redirect, withRouter } from 'react-router-dom';
import { connect, useSelector } from 'react-redux';
import { debounce } from 'lodash';

import ActionBar from '../../components/ActionBar';
import EsmsPage from '../../components/EsmsPage';
import ScoreDebuggerTable from './ScoreDebuggerTable';
import { TimestampFilter } from '../../components/RuleExceptionsTable';


import ScoreTogglePanel from './ScoreTogglePanel';
import useTableParams from '../../hooks/useTableParams';

import { fetchScoring, toggleScoring } from '../../actions/adminActions';


function parseEndTimestamp(searchParams) {
    const params = new URLSearchParams(searchParams);
    const endDate = moment(params.get('endTimestamp') || undefined).local();
    if (!params.has('endTimestamp')) {
        endDate.endOf('day');
    }
    return endDate;
}

function parseStartTimestamp(searchParams) {
    const params = new URLSearchParams(searchParams);
    const startDate = moment(params.get('startTimestamp') || undefined).local();
    if (!params.has('startTimestamp')) {
        startDate.startOf('day');
    }
    return startDate;
}

const ScoringPage = ({ history, location, scoring, fetchScoring, toggleScoring }) => {

    const companyInfo = useSelector(
        ({ companyInfo }) => companyInfo
    );
    const [redirectTo, setRedirectTo] = useState(null);
    const [startTimestamp, setStartTimestamp] =
        useState(parseStartTimestamp(location.search));
    const [endTimestamp, setEndTimestamp] =
        useState(parseEndTimestamp(location.search));

    const {
        currentPage,
        onCurrentPageChange,
        onExit,
        onPageSizeChange,
        pageSize,
        pageSizes,
        totalCount
    } = useTableParams(scoring);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const endTimestamp = parseEndTimestamp(params);
        const startTimestamp = parseStartTimestamp(params);
        setEndTimestamp(endTimestamp);
        setStartTimestamp(startTimestamp);
        fetchScoring({
            endTimestamp:   endTimestamp.toISOString(),
            startTimestamp: startTimestamp.toISOString()
        });
        return () => {
            '';
        };
    }, [location.search]);

    useEffect(() => {
        return () => {
            onExit();
        };
    }, []);

    function getResetQuerystring() {
        const params = new URLSearchParams(location.search);
        params.delete('endTimestamp');
        params.delete('startTimestamp');
        params.delete('page');
        params.delete('pageSize');
        params.delete('orderby');
        return params;
    }

    function handleFilterChange(filterName, value) {
        const newParams = new URLSearchParams(location.search);
        if (value) {
            newParams.set(filterName, value.toISOString());
        } else {
            newParams.delete(filterName);
        }
        newParams.delete('page');
        history.replace({
            pathname: location.pathname,
            search:   newParams.toString()
        });
    }

    function linkToAdmin() {
        const params = getResetQuerystring();
        setRedirectTo({
            pathname: '/admin',
            search:   params.toString()
        });
    }

    const handleToggle = debounce(() => {
        toggleScoring(companyInfo);
    }, 500);

    if (redirectTo) {
        return (
            <Redirect push to={redirectTo} />
        );
    }

    return (
        <EsmsPage
            parents={[
                companyInfo.companyName
            ]}
            title="Score Debugger"
            handlers={[linkToAdmin]}
            sidebar={(
                <React.Fragment>
                    <ScoreTogglePanel
                        isScoringLoading={scoring.toggleScoringLoading}
                        toggleScoring={handleToggle}
                        scoringEnabled={companyInfo.scoringEnabled}
                    />
                </React.Fragment>
            )}
        >
            <ActionBar
                startAdornments={(
                    <TimestampFilter
                        endTimestamp={endTimestamp}
                        onFilterChange={handleFilterChange}
                        startTimestamp={startTimestamp}
                    />
                )}
            />
            <ScoreDebuggerTable
                currentPage={currentPage}
                error={scoring.error}
                scoringLogs={scoring.items|| []}
                isLoading={scoring.loading}
                onCurrentPageChange={onCurrentPageChange}
                onPageSizeChange={onPageSizeChange}
                pageSize={pageSize}
                pageSizes={pageSizes}
                totalCount={totalCount}
            />
        </EsmsPage>
    );
};

ScoringPage.propTypes = {
    history: shape({
        replace: func
    }),
    location: shape({
        search: string
    }),
    match: shape({
        params: shape({
            id: string
        })
    })
};


const mapStateToProps = state => ({
    scoring:     state.scoring,
    companyInfo: state.companyInfo
});

const mapDispatchToProps = {
    fetchScoring,
    toggleScoring
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ScoringPage));
